import type { AIActionType } from '@aibi/contracts';

/**
 * Codigos cerrados de error del AI Core. Son estables para la UI y la auditoria y nunca
 * incluyen valores de datos ni secretos: el mensaje describe el problema, no el contenido.
 */
export const AI_ERROR_CODES = [
  'FIELD_NOT_FOUND',
  'VALUE_NOT_FOUND',
  'FILTER_NOT_FOUND',
  'AMBIGUOUS_FIELD',
  'UNRECOGNIZED_INTENT',
  'ACTION_NOT_ALLOWED',
  'UNSAFE_CONTENT',
  'LIMIT_EXCEEDED',
  'INVALID_SCHEMA',
  'SEMANTIC_ERROR',
  'STATE_INVALID',
  'PROVIDER_ERROR',
  'CANCELLED',
] as const;
export type AIErrorCode = (typeof AI_ERROR_CODES)[number];

/** Candidato propuesto al usuario para resolver una ambiguedad o un campo inexistente. */
export interface AICandidate {
  /** Id del objeto del modelo (dimension, medida) o tipo de visualizacion. */
  readonly id: string;
  readonly label: string;
  readonly kind: 'dimension' | 'measure' | 'member' | 'visualization' | 'filter';
  /** Solo para `member`: dimension a la que pertenece el valor. */
  readonly dimensionId?: string;
}

/** Pregunta de aclaracion: la IA no adivina cuando hay varias interpretaciones validas. */
export interface AIClarification {
  readonly question: string;
  readonly candidates: readonly AICandidate[];
  readonly reason: string;
  /** Acciones que dependen de la respuesta del usuario. */
  readonly affectedActionIds: readonly string[];
  /** Termino normalizado del prompt que origino la ambiguedad (permite reformular). */
  readonly term?: string;
}

/** Accion que no pudo construirse o validarse. */
export interface AIActionError {
  readonly code: AIErrorCode;
  readonly message: string;
  readonly attemptedAction: {
    readonly type: AIActionType | 'unknown';
    /** Termino del usuario que se intento resolver (nunca contenido inseguro). */
    readonly term?: string;
  };
  readonly candidates?: readonly AICandidate[];
}

/** Etapas del pipeline de validacion, en orden de ejecucion. */
export const AI_VALIDATION_STAGES = [
  'input',
  'size',
  'parse',
  'guardrails',
  'schema',
  'action',
  'semantic',
  'sandbox',
  'analysis-state',
  'analysis-engine',
] as const;
export type AIValidationStage = (typeof AI_VALIDATION_STAGES)[number];

/** Diagnostico preciso de una etapa; `actionId` identifica la accion culpable. */
export interface AIDiagnostic {
  readonly stage: AIValidationStage;
  readonly code: AIErrorCode;
  readonly path: string;
  readonly message: string;
  readonly actionId?: string;
}

export const actionError = (
  code: AIErrorCode,
  message: string,
  attemptedAction: AIActionError['attemptedAction'],
  candidates?: readonly AICandidate[],
): AIActionError => ({
  code,
  message,
  attemptedAction,
  ...(candidates === undefined || candidates.length === 0 ? {} : { candidates }),
});
